import { useEffect, useRef } from 'react';

const countries = [
  {
    name: 'Türkiye',
    flag: '🇹🇷',
    delivery: 'Yurtiçi',
    cities: 'İstanbul, Ankara, İzmir, Gaziantep',
    bg: 'bg-red-50',
    accent: 'text-red-800',
  },
  {
    name: 'Suriye',
    flag: '🇸🇾',
    delivery: '1-2 Gün',
    cities: 'Şam, Halep, Lazkiye',
    bg: 'bg-green-50',
    accent: 'text-green-800',
  },
  {
    name: 'Azerbaycan',
    flag: '🇦🇿',
    delivery: '2-3 Gün',
    cities: 'Bakü, Gence, Sumgayıt',
    bg: 'bg-orange-50',
    accent: 'text-orange-800',
  },
  {
    name: 'Irak',
    flag: '🇮🇶',
    delivery: '2-4 Gün',
    cities: 'Bağdat, Erbil, Musul',
    bg: 'bg-yellow-50',
    accent: 'text-yellow-800',
  },
  {
    name: 'İran',
    flag: '🇮🇷',
    delivery: '3-5 Gün',
    cities: 'Tahran, Tebriz, İsfahan',
    bg: 'bg-gray-50',
    accent: 'text-gray-800',
  },
  {
    name: 'Gürcistan',
    flag: '🇬🇪',
    delivery: '2-3 Gün',
    cities: 'Tiflis, Batum, Kutaisi',
    bg: 'bg-rose-50',
    accent: 'text-rose-800',
  },
  {
    name: 'Ermenistan',
    flag: '🇦🇲',
    delivery: '2-4 Gün',
    cities: 'Erivan, Gümrü, Vanadzor',
    bg: 'bg-amber-50',
    accent: 'text-amber-800',
  },
  {
    name: 'Rusya',
    flag: '🇷🇺',
    delivery: '5-8 Gün',
    cities: 'Moskova, St. Petersburg, Kazan',
    bg: 'bg-slate-50',
    accent: 'text-slate-800',
  },
];

export default function Countries() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('opacity-100', 'translate-y-0');
            entry.target.classList.remove('opacity-0', 'translate-y-10');
          }
        });
      },
      { threshold: 0.1 }
    );
    const children = sectionRef.current?.querySelectorAll('.animate-on-scroll');
    children?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section id="hedef-ulkeler" className="py-24 bg-white" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-16 animate-on-scroll opacity-0 translate-y-10 transition-all duration-700">
          <span className="text-orange-500 text-sm font-bold uppercase tracking-widest mb-3 block">
            Hedef Ülkeler
          </span>
          <h2
            className="text-4xl md:text-5xl font-extrabold text-gray-900 leading-tight mb-4"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            Hizmet Verdiğimiz <span className="text-orange-500">Ülkeler</span>
          </h2>
          <p className="text-gray-500 max-w-xl mx-auto text-base">
            Yurtiçi, yurtdışı ve uluslararası seferlerimiz ile 8 ülkeye güvenli ve zamanında teslimat yapıyoruz.
          </p>
        </div>

        {/* Country Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {countries.map((c, i) => (
            <div
              key={c.name}
              className={`animate-on-scroll opacity-0 translate-y-10 transition-all duration-700 ${c.bg} rounded-2xl p-8 hover:-translate-y-2 cursor-pointer group`}
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              <div className="flex items-start justify-between mb-6">
                <span className="text-6xl">{c.flag}</span>
                <span className="flex items-center gap-1.5 bg-white rounded-full px-3 py-1.5 text-xs font-bold text-gray-600 shadow-sm">
                  <i className="ri-time-line text-orange-500" />
                  {c.delivery}
                </span>
              </div>
              <h3
                className={`text-2xl font-extrabold ${c.accent} mb-2`}
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                {c.name}
              </h3>
              <p className="text-gray-500 text-sm mb-4">{c.cities}</p>
              <div className="flex items-center gap-2 text-orange-500 font-semibold text-sm group-hover:gap-3 transition-all">
                <i className="ri-map-pin-line" />
                Düzenli Sefer
              </div>
            </div>
          ))}
        </div>

        {/* Map Banner */}
        <div className="mt-16 animate-on-scroll opacity-0 translate-y-10 transition-all duration-700 rounded-2xl overflow-hidden h-72 relative">
          <img
            src="https://readdy.ai/api/search-image?query=Professional%20aerial%20view%20of%20modern%20cargo%20trucks%20driving%20on%20multiple%20highways%20connecting%20Turkey%20to%20Middle%20East%20and%20Caucasus%20countries%2C%20logistics%20transportation%20network%20with%20warm%20orange%20sunset%20lighting%2C%20dramatic%20perspective%20showing%20fleet%20of%20trucks%20on%20open%20roads%2C%20clean%20simple%20sky%20background%2C%20high%20quality%20commercial%20photography%2C%20ultra%20realistic&width=1200&height=400&seq=map003&orientation=landscape"
            alt="Uluslararası Lojistik Taşımacılık"
            loading="eager"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/80 to-transparent flex items-center px-10">
            <div>
              <h3 className="text-white text-2xl md:text-3xl font-extrabold mb-2">
                Yurtiçi, Yurtdışı ve Uluslararası Taşımacılık
              </h3>
              <p className="text-gray-300 text-sm max-w-md">
                Kilis&apos;ten yola çıkan yükleriniz, güvenli rotalarımızla hedef ülkelere zamanında ulaşır.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
